import { Container, Service } from 'typedi';
import { LoggerProvider } from '../logger';
import {
    IAppClosedTopicPayload,
    IRouterChannel,
    ISessionChannel,
    MessageBroker,
    INavigateByCodeTopicPayload,
    UserMode,
    ILogger,
    ITopicSubscription,
    INavigationChannel,
} from '@sparkware/uc-sdk-core';
import {
    IdsOfElements,
    NAVIGATION_IGNORE_ATTRIBUTE,
    IMAGE_LINK_REGEX,
    ClientProvider,
    StaticPaths,
    ViewSource,
    Features,
    Brand,
    CloseReason,
} from '../../models/enums/general-enums';
import { FeatureAvailabilityToken, UserContextToken, WindowToken } from '../../injection-tokens';
import ContentApi from '../../../APIs/ContentApi';
import { IUrlHandlerOptions } from './models/IUrlHandlerOptions';
import { Navigation } from '../navigation';
import { Utils } from '../utils';
import { ViewInjector } from '../view-injector';
import { WebLoginService } from '../web-login/web.login.service';
import { UserareaService } from '../external/userarea';
import { UrlUtils } from '../utils/urlUtils';
import { RouterUtils } from './router.utils';
import { IBIEventHandler } from '../bi/models/IBIEventHandler';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';
import { SiteSegmentationContext } from '../site-segmentation-context';
import { IAppCatalog } from '../app-catalog/interfaces';
import { BrandUtils } from '../../../Modules/Utils/BrandUtils';
import { ICRM } from '../crm/models';
import { IClientIntegrationFacade } from '../client-integration/interfaces/IClientIntegrationFacade';
import { LoaderManager } from '../../loaders/LoaderManager';
import { IAppLauncher } from '../app-launcher/models';
import { IUserContext } from '../user-context/user-context-interface';
import StringHelper from '../../../Modules/Utils/StringUtils';
import { IPreloaderManager } from '../ui/preloader/models/interfaces/IPreloaderManager';
import { FlowsRunner } from '../flows-runner';

@Service()
export class Router {
    private _lastUrl: string;
    private _navigationByCodeSubscription: ITopicSubscription;
    private _changeBrowserURISubscription: ITopicSubscription;
    private readonly _logger: ILogger;
    private readonly _viewInjector: ViewInjector;
    private readonly _window: Window;
    private readonly _utils: Utils;
    private readonly _navigation: Navigation;
    private readonly _clientProvider: string;
    private readonly _userAreaService: UserareaService;
    private readonly _routerUtils: RouterUtils;
    private readonly _urlUtils: UrlUtils;
    private readonly _featureAvailability: IFeatureAvailability;
    private readonly _siteSegmentationContext: SiteSegmentationContext;
    private readonly _userContext: IUserContext;
    private readonly _webLoginService: WebLoginService;
    private readonly _flowsRunner: FlowsRunner;
    private readonly _routerChannel: IRouterChannel;
    private readonly _sessionChannel: ISessionChannel;
    private readonly _navigationChannel: INavigationChannel;

    private get _crmPromise(): Promise<ICRM> {
        return LoaderManager.Instance.CRMLoader.Instance;
    }

    private get _appCatalogPromise(): Promise<IAppCatalog> {
        return LoaderManager.Instance.AppCatalogLoader.Instance;
    }

    private get _appLauncherPromise(): Promise<IAppLauncher> {
        return LoaderManager.Instance.AppLauncherLoader.Instance;
    }

    private get _biEventHandlerPromise(): Promise<IBIEventHandler> {
        return LoaderManager.Instance.BIEventHandlerLoader.Instance;
    }

    private get _preloaderManagerPromise(): Promise<IPreloaderManager> {
        return LoaderManager.Instance.PreloaderManagerLoader.Instance;
    }

    private get _clientIntegrationFacadePromise(): Promise<IClientIntegrationFacade> {
        return LoaderManager.Instance.ClientIntegrationFacadeLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('Router');
        this._window = Container.get(WindowToken);
        this._viewInjector = Container.get(ViewInjector);
        this._utils = Container.get(Utils);
        this._urlUtils = Container.get(UrlUtils);
        this._navigation = Container.get(Navigation);
        this._userAreaService = Container.get(UserareaService);
        this._routerUtils = Container.get(RouterUtils);
        this._clientProvider = this._utils.getClientProvider();
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._siteSegmentationContext = Container.get(SiteSegmentationContext);
        this._userContext = Container.get(UserContextToken);
        this._webLoginService = Container.get(WebLoginService);
        this._flowsRunner = Container.get(FlowsRunner);
        this._routerChannel = MessageBroker.getInstance().router;
        this._sessionChannel = MessageBroker.getInstance().session;
        this._navigationChannel = MessageBroker.getInstance().navigation;
    }

    public async checkForDeepLinkHash(): Promise<void> {
        this.enable();
        const deepLinkHash = this._urlUtils.getDeeplinkHash();
        if (deepLinkHash) {
            await this._userAreaService.executeOnload(async () => {
                await this._flowsRunner.run(deepLinkHash);
                const newUrl = this._urlUtils.removeParamsFromQuery(
                    this._urlUtils.getCurrentUrl(),
                    'deeplink',
                );
                this._urlUtils.historyReplaceState(this._window.document.title, newUrl);
            });
        }
    }

    public async handleFirstHit(): Promise<void> {
        await this.NavigateTo(this._urlUtils.getRelativeURL(this._routerUtils.URI));
    }

    public disable() {
        this._toggleURLHandler(true);
    }

    public async NavigateTo(url: string): Promise<void> {
        return await this._urlHandler(url, { clientNavigated: false });
    }

    public async handleMainViewApp(url: string = null): Promise<boolean> {
        let needRedirect: boolean = false;
        const currentLocation = this._urlUtils.getNoLangURL(
            this._urlUtils.getURLWithoutHost(url ? url : this._window.location.href),
        );
        if (currentLocation?.includes('app/')) {
            const appCatalog = await this._appCatalogPromise;
            await appCatalog.getAppsCatalog();
            const appNavigation = await appCatalog.getAppNavigationData(currentLocation);

            if (!!appNavigation) {
                const appData = await appCatalog.getAppCatalogData(appNavigation.appID);
                if (
                    appData.app.userMode == UserMode.Authenticated &&
                    !this._userContext.IsAuthenticated
                ) {
                    this._sessionChannel.topics.appClosing.publish({ publisher: 'RouterService' }, {
                        appID: appNavigation?.appID,
                    } as IAppClosedTopicPayload);

                    const navigationUrl = BrandUtils.isPokerClient()
                        ? StaticPaths.PokerRoot
                        : StaticPaths.Root;

                    await this.NavigateTo(navigationUrl);
                    if (!StringHelper.isNullOrWhiteSpaces(url)) {
                        this._urlUtils.historyPushState(
                            {},
                            this._window.document.title,
                            this._urlUtils.getLangURL(navigationUrl),
                        );
                    }
                    needRedirect = true;
                }
            }
        }
        return needRedirect;
    }

    public handleCodeNavigation = async (data: INavigateByCodeTopicPayload): Promise<void> => {
        this._sessionChannel.topics.webLoginClosed.publish({ publisher: 'UCF.RouterService' }, {});
        if (!data) {
            this._logger.warn(`handleCodeNavigation: Invalid data, execution will stop.`);
            return;
        }

        this._utils.ScrollToTop();
        if (!data.url.startsWith('/')) {
            data.url = `/${data.url}`;
        }
        const isClientNavigation = this._routerUtils.isClientNavigation(data.url);
        if (!isClientNavigation) {
            const { brandId } = this._window.pageContextManager.getBrandData();
            if (brandId === Brand.Sport_MG_DK) {
                data.url = this._urlUtils.getURLWithBase(data.url);
            }
        }
        this._logger.debug(`Handling code navigation - navigating to "${data.url}"`);
        if (this._utils.findIfIsNative()) {
            await this._closeOverlappingApps(data.url);
        }
        const options: IUrlHandlerOptions = {
            clientNavigated: false,
            isReplaceState: !!data?.isReplaceState,
            state: data?.state,
        };
        await this._urlHandler(data.url, options);
    };

    private enable() {
        this._toggleURLHandler(false);
    }

    private handleClickEvents = async (event: MouseEvent): Promise<void> => {
        const elementTarget = event.target as HTMLElement;
        if (this._isNavigationIgnored(elementTarget)) return;

        const closestLink =
            typeof elementTarget?.closest === 'function' ? elementTarget.closest('a') : undefined;
        if (!closestLink) return;

        const url = closestLink.getAttribute('href');
        const target = closestLink.getAttribute('target');
        if (!url) return;

        this._utils.ScrollToTop();
        const parsedUrl = this._urlUtils.getParsedUrl(url);
        const query = Object.fromEntries(parsedUrl.searchParams.entries());
        if (parsedUrl.protocol?.startsWith('javascript')) return;

        if (IMAGE_LINK_REGEX.test(url)) {
            event.preventDefault();
            return;
        }

        if (this._isSameLocation(parsedUrl)) {
            this._logger.debug(
                'Trying to navigate to the same location. Stopping event from propagation...',
            );
            event.preventDefault();
            void this._clientIntegrationFacadePromise.then((facade) => facade.navigatePublish(url));
            return;
        }

        this._logger.debug(`Handling click event - navigating to "${url || '[Invalid link]'}"`);
        const urlHandlerOptions: IUrlHandlerOptions = { event, clientNavigated: false, target };
        if (query.deeplink) {
            await this._navigation.HandleDeeplinks(url);
            if (parsedUrl.pathname === null) {
                event.preventDefault();
                return;
            }
            await this._urlHandler(parsedUrl.pathname, urlHandlerOptions);
        } else {
            await this._urlHandler(url, urlHandlerOptions);
        }
    };

    private handlePopStateEvents = async (event: PopStateEvent): Promise<void> => {
        this._logger.debug(
            `Handling popstate event - navigating to "${this._window.location.href}"`,
        );
        const url = this._urlUtils.getURLWithoutHost(this._window.location.href);
        const parsedUrl = this._urlUtils.getParsedUrl(url);
        const parsedLastUrl = this._lastUrl
            ? this._urlUtils.getParsedUrl(this._lastUrl)
            : undefined;
        this._handleMainViewAppClosing(parsedLastUrl).catch((err) => {
            this._logger.error(`failed to run _handleMainViewAppClosing function`, err);
        });
        if (parsedUrl.pathname === parsedLastUrl?.pathname) return;

        const options: IUrlHandlerOptions = {
            event,
            clientNavigated: false,
        };
        await this._urlHandler(url, options);
        this._utils.ScrollToTop();
    };

    // todo - change  changeBrowserURI topic to readonly changeBrowserURI: ITopic<INavigateData>; and then support it here
    private handleChangeBrowserURI = async (data: { uri: string }): Promise<void> => {
        const { uri } = data;
        this._utils.ScrollToTop();
        this._logger.debug(`Handling change browser uri event - navigating to: "${uri}"`);
        const url = this._urlUtils.getURLWithoutHost(uri);
        const parsedUrl = this._urlUtils.getParsedUrl(url);
        const parsedLastUrl = this._lastUrl
            ? this._urlUtils.getParsedUrl(this._lastUrl)
            : undefined;
        if (parsedUrl.pathname === parsedLastUrl?.pathname) {
            return;
        }
        const options: IUrlHandlerOptions = {
            clientNavigated: true,
        };
        await this._urlHandler(url, options);
    };

    private _toggleURLHandler(isRemoving: boolean): void {
        const eventListenerFunc = isRemoving
            ? this._window.removeEventListener
            : this._window.addEventListener;

        this._logger.debug(
            `${isRemoving ? 'Unregistering' : 'Registering'} navigation click events...`,
        );
        eventListenerFunc('click', this.handleClickEvents, true);
        this._logger.debug(
            `${isRemoving ? 'Unregistering' : 'Registering'} navigation popstate events...`,
        );
        eventListenerFunc('popstate', this.handlePopStateEvents, true);
        this._logger.debug(
            `${isRemoving ? 'Unregistering' : 'Registering'} navigation by code events...`,
        );
        if (isRemoving) {
            if (this._navigationByCodeSubscription)
                this._navigationByCodeSubscription.unsubscribe();
        } else {
            this._navigationByCodeSubscription =
                this._routerChannel.topics.NavigateByCode.subscribe(this.handleCodeNavigation);
        }
        this._logger.debug(
            `${isRemoving ? 'Unregistering' : 'Registering'} change browser uri events...`,
        );
        if (isRemoving) {
            if (this._changeBrowserURISubscription)
                this._changeBrowserURISubscription.unsubscribe();
        } else {
            this._changeBrowserURISubscription =
                this._navigationChannel.topics.changeBrowserURI.subscribe(
                    this.handleChangeBrowserURI,
                );
        }
    }

    private _isNavigationIgnored = (element: HTMLElement): boolean => {
        if (!element) return false;

        if (
            typeof element.getAttribute === 'function' &&
            element.getAttribute(NAVIGATION_IGNORE_ATTRIBUTE) === 'true'
        ) {
            return true;
        }

        return this._isNavigationIgnored(element.parentElement);
    };

    private _computeRelativeUrl(url: string) {
        const parsedUrl = this._urlUtils.getParsedUrl(url);
        if (!parsedUrl.pathname.includes('.') && !parsedUrl.pathname.endsWith('/')) {
            parsedUrl.pathname = parsedUrl.pathname + '/';
        }
        return this._urlUtils.getURLWithoutHost(parsedUrl.toString());
    }

    private async _urlHandler(
        url: string,
        options: IUrlHandlerOptions = { clientNavigated: false, isReplaceState: false },
    ): Promise<void> {
        const { target, event } = options;
        this._logger.debug(`NavigationURL: ${decodeURIComponent(url)}`);
        const preloaderManager = await this._preloaderManagerPromise;
        preloaderManager?.onRemoveFailed();

        if (this._isManagedNavigation(url, target)) {
            this._handleMainViewAppClosing().catch((err) => {
                this._logger.error(`failed to run _handleMainViewAppClosing function`, err);
            });
            let relativeUrl = this._computeRelativeUrl(url);
            this._lastUrl = relativeUrl;

            this._routerChannel.topics.Navigate.publish(
                {
                    publisher: 'UCF.RouterService',
                },
                decodeURIComponent(this._urlUtils.getNoLangURL(relativeUrl)),
            );

            this._routerChannel.topics.NavigateV2.publish(
                {
                    publisher: 'UCF.RouterService',
                },
                {
                    url: decodeURIComponent(this._urlUtils.getNoLangURL(relativeUrl)),
                    state: options.state ?? this._window.history.state,
                },
            );

            if (event) {
                this._logger.debug(
                    'Stopping default browser behavior. Handling custom navigation...',
                );
                event.preventDefault();
            }

            if (
                this._viewInjector.ActiveView != ViewSource.FirstHit &&
                !(event instanceof PopStateEvent) &&
                ((this._clientProvider === ClientProvider.Kambi &&
                    !this._routerUtils.isClientNavigation(relativeUrl)) ||
                    this._clientProvider === ClientProvider.Spectate ||
                    this._clientProvider === ClientProvider.Poker)
            ) {
                if (options.isReplaceState) {
                    this._urlUtils.historyReplaceState(
                        this._window.document.title,
                        this._urlUtils.getLangURL(relativeUrl),
                    );
                } else {
                    this._urlUtils.historyPushState(
                        {},
                        this._window.document.title,
                        this._urlUtils.getLangURL(relativeUrl),
                    );
                }
            }

            void this._sendNavigationRequestEvent(relativeUrl);
            await this._handleNavigation(relativeUrl, options);
        } else
            this._logger.debug(
                `Navigation to ${decodeURIComponent(url)} is not managed by RouterModule.`,
            );
    }

    private async _handleNavigation(url: string, options?: IUrlHandlerOptions): Promise<void> {
        if (!(await this.handleMainViewApp(url))) {
            this._logger.debug(`Managing navigation for ${url}.`);

            const isClientNavigation = this._routerUtils.isClientNavigation(url);
            this._viewInjector.ResetView();

            if (await this._handleAppOpening(this._urlUtils.getNoLangURL(url))) {
                this._logger.debug(`Calling ViewInjector's function called: HideClientAndContent`);
                this._viewInjector.SetActiveView(ViewSource.Content);
                this._viewInjector.HideClientAndContent();
                this._utils.ClearElementWithId(IdsOfElements.UcFullContent);

                return;
            } else {
                this._viewInjector.ShowClientAndContent(isClientNavigation);
            }

            if (isClientNavigation) {
                await this._viewInjector.ShowClient(url, options);
            } else {
                const appCatalog = await this._appCatalogPromise;
                const appNavigationData = await appCatalog.getAppNavigationData(
                    this._urlUtils.getNoLangURL(url),
                );
                if (!!appNavigationData) {
                    const { actionID, pageTitle } = appNavigationData;

                    await this._userAreaService.executeOnload(() => {
                        this._viewInjector.ShowMainAppView(actionID, pageTitle);
                    });
                } else await this._viewInjector.ShowContent(url, options);
            }

            this._logger.debug(
                `Calling ViewInjector's function called: ${
                    isClientNavigation ? 'ShowClient' : 'ShowContent'
                }`,
            );
        }
    }

    private async _handleAppOpening(url: string) {
        const surveyEnabled = this._featureAvailability.IsFeatureEnabled(Features.SURVEY);
        if (surveyEnabled) {
            const parsedUrl = this._urlUtils.getParsedUrl(url);
            if (
                this._getSurveyUrls().some((surveyUrl) => {
                    const encodedSurveyUrl = this._urlUtils.getURLWithBase(encodeURI(surveyUrl));
                    return (
                        encodedSurveyUrl === parsedUrl.pathname ||
                        encodedSurveyUrl === parsedUrl.hash
                    );
                })
            ) {
                (await this._crmPromise).OpenSurvey('router.service');
                await this._setSurveyTitle(parsedUrl);
                return true;
            }
        }

        return false;
    }

    private _getSurveyUrls() {
        const surveyEnabled = this._featureAvailability.IsFeatureEnabled(Features.SURVEY);
        const { clientPage, oldClientPage } = this._window.pageContextManager.getSurveyData() || {};

        return (surveyEnabled && [clientPage, oldClientPage].filter((link) => !!link)) || [];
    }

    private async _setSurveyTitle(parsedUrl) {
        const { publicationId } = this._window.pageContextManager.getInfraData();

        const siteSegmentationContext = await this._siteSegmentationContext.getString();
        let { response, errorResponse } = await ContentApi.GetPageContent(
            parsedUrl.pathname,
            publicationId,
            siteSegmentationContext,
        );

        if (errorResponse) {
            this._logger.error(
                `Error while fetching survey content for ${parsedUrl.pathname}: ${errorResponse}`,
            );
        }

        this._viewInjector.handleContentApiResponse(response);
        document.title = response?.title || '';
    }

    private _isManagedNavigation(url: string, target?: string): boolean {
        if (!url) return false;
        const parsedUrl = this._urlUtils.getParsedUrlV2(url);
        if (!parsedUrl) return false;

        const isHashRoute = parsedUrl.hash && parsedUrl.hash.startsWith('#/');
        const isHashOnly =
            parsedUrl.hash && parsedUrl.pathname === this._window.location.pathname && !isHashRoute;
        if (
            target === '_blank' ||
            (parsedUrl.hostname && parsedUrl.hostname !== this._window.location.hostname) ||
            isHashOnly
        ) {
            return false;
        }
        return true;
    }

    private _isSameLocation = (parsedUrl) => {
        if (parsedUrl.hostname && parsedUrl.hostname !== this._window.location.hostname)
            return false;

        const urlWithoutHost = this._urlUtils.getURLWithoutHost(parsedUrl.href);
        const currentLocation = this._urlUtils.getURLWithoutHost(this._window.location.href);

        return encodeURI(urlWithoutHost) === currentLocation;
    };

    private async _handleMainViewAppClosing(parsedUrl?) {
        const currentLocation = !!parsedUrl
            ? this._urlUtils.getNoLangURL(parsedUrl?.href)
            : this._urlUtils.getNoLangURL(
                  this._urlUtils.getURLWithoutHost(this._window.location.href),
              );

        const appCatalog = await this._appCatalogPromise;
        const appNavigation = await appCatalog.getAppNavigationData(currentLocation);

        if (!!appNavigation) {
            this._sessionChannel.topics.appClosing.publish({ publisher: 'RouterService' }, {
                appID: appNavigation?.appID,
            } as IAppClosedTopicPayload);
        }
    }

    private async _sendNavigationRequestEvent(url: string) {
        const biEventHandler = await this._biEventHandlerPromise;
        biEventHandler?.sendNavigationRequestEvent(url);
    }

    private async _closeOverlappingApps(url: string) {
        await this._webLoginService.Hide();
        await this._viewInjector.CloseWrapDiv();
        const appLauncher = await this._appLauncherPromise;
        void appLauncher.CloseOpenedApps(CloseReason.userClosedApp, {
            source: 'Unified Client',
            area: 'Overlay',
        });
        const logoDiv = this._window.document.getElementById('logo');
        if (logoDiv) logoDiv.click();
        const userMenu = this._window.document.getElementsByClassName(
            'mobileAcUserMenu mobileAcUserMenuShow do-not-close-account-drawer',
        )[0];
        if (!!userMenu) {
            const userMenuCloseButton = this._window.document.getElementsByClassName(
                'aCuserMenuClose',
            )[0] as HTMLElement;
            if (userMenuCloseButton) {
                userMenuCloseButton.click();
            }
        }
        if (!this._routerUtils.isClientNavigation(url)) {
            const betSlipClose = this._window.document.getElementById('betslip-close');
            if (betSlipClose) {
                betSlipClose.click();
            }
        }
    }
}
